/**
 * AdminAgents — Admin panel showing all agents with expandable detail view
 */
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { SkeletonBar, Button, Input } from '../components/ui';
import { T } from '../styles/tokens';

interface AgentRow {
  id: string;
  machine_id: string;
  customer_id: string;
  agent_name: string | null;
  agent_model: string | null;
  agent_provider: string | null;
  agent_language: string | null;
  agent_brain: string | null;
  tools: string | null;
  skills: string | null;
  channels: string | null;
  integrations: string | null;
  env_keys: string | null;
  oc_path: string | null;
  is_docker: number;
  node_version: string | null;
  openclaw_version: string | null;
  last_auto_sync: string | null;
  config_json: string | null;
  agents_md: string | null;
  heartbeat_md: string | null;
  identity_md: string | null;
  bootstrap_md: string | null;
  env_path: string | null;
  config_path: string | null;
  synced_at: string;
  machine_name: string | null;
  machine_ip: string | null;
  machine_status: string | null;
  machine_display_name: string | null;
  customer_email: string | null;
}

interface BrainSnapshot {
  soul_md: string | null;
  user_md: string | null;
  memory_md: string | null;
  tools_md: string | null;
  snapshot_at: string | null;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'agora';
  if (diff < 60_000) return `${Math.round(diff / 1000)}s atrás`;
  if (diff < 3600_000) return `${Math.round(diff / 60000)}min atrás`;
  if (diff < 86400_000) return `${Math.round(diff / 3600000)}h atrás`;
  return `${Math.floor(diff / 86400000)}d atrás`;
}

function AgentAvatar({ name, size = 36 }: { name: string; size?: number }) {
  const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue = hash % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: `linear-gradient(135deg, hsl(${hue}, 45%, 40%), hsl(${(hue + 40) % 360}, 45%, 35%))`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, fontWeight: 800, color: T.text, flexShrink: 0,
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function Badge({ text, color = T.textMuted }: { text: string; color?: string }) {
  return (
    <span style={{
      padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: `${color}15`, color, border: `1px solid ${color}30`,
      display: 'inline-flex', alignItems: 'center',
    }}>{text}</span>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: T.textMuted, fontSize: 11 }}>{label}</span>
      <span style={{ color: T.textSecondary, fontSize: 11, fontFamily: mono ? 'monospace' : 'inherit', maxWidth: '60%', textAlign: 'right' as const, wordBreak: 'break-all' as const }}>{value}</span>
    </div>
  );
}

function MdBlock({ title, content }: { title: string; content: string | null }) {
  const [open, setOpen] = useState(false);
  if (!content) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.purple, padding: '4px 0', userSelect: 'none' }}
      >
        {open ? '▾' : '▸'} {title} ({(content.length / 1024).toFixed(1)}KB)
      </div>
      {open && (
        <pre style={{
          background: T.blackAlpha30, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: 12, fontSize: 11, color: T.text,
          maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const,
          fontFamily: 'monospace', margin: '4px 0 0',
        }}>{content}</pre>
      )}
    </div>
  );
}

interface LLMUsage {
  customer_id: string;
  period: string;
  since: string;
  totals: { total_requests: number; total_input_tokens: number; total_output_tokens: number; total_cost: number; total_charged: number };
  by_model: Array<{ model: string; provider: string; requests: number; total_input_tokens: number; total_output_tokens: number; total_cost: number; total_charged: number; avg_latency: number }>;
  daily: Array<{ date: string; requests: number; input_tokens: number; output_tokens: number; cost: number; charged: number }>;
  tokens: Array<{ id: string; name: string; prefix: string }>;
}

function formatUSD(n: number): string {
  return `$${n.toFixed(4)}`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n || 0);
}

const CHANNEL_ICONS: Record<string, { icon: string; color: string }> = {
 telegram: { icon: '', color: '#229ED9' },
 whatsapp: { icon: '', color: '#25D366' },
 discord: { icon: '', color: '#5865F2' },
 slack: { icon: '', color: '#4A154B' },
 signal: { icon: '', color: '#3A76F0' },
 imessage: { icon: '', color: '#34C759' },
 irc: { icon: '', color: T.textMuted },
 googlechat: { icon: '', color: '#00AC47' },
 nostr: { icon: '', color: T.purple },
 web: { icon: '', color: T.primary },
};

const TABS = [
  { id: 'geral', label: 'Geral', color: T.primary },
  { id: 'creds', label: 'Credenciais', color: T.success },
  { id: 'channels', label: 'Canais', color: T.danger },
  { id: 'llm', label: 'Gastos LLM', color: T.warning },
  { id: 'brain', label: 'Cérebro', color: T.purple },
  { id: 'tools', label: 'Tools & Skills', color: T.accent },
] as const;

type TabId = typeof TABS[number]['id'];

function ExpandedAgent({ agent, parseJson }: { agent: AgentRow; parseJson: (v: string | null) => string[] }) {
  const [tab, setTab] = useState<TabId>('geral');
  const [brain, setBrain] = useState<BrainSnapshot | null>(null);
  const [brainLoading, setBrainLoading] = useState(false);
  const [brainError, setBrainError] = useState('');
  const [llmUsage, setLlmUsage] = useState<LLMUsage | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmError, setLlmError] = useState('');

  const tools = parseJson(agent.tools);
  const skills = parseJson(agent.skills);
  const channels = parseJson(agent.channels);
  const integrations = parseJson(agent.integrations);
  const envKeys = parseJson(agent.env_keys);

  let configParsed: string | null = null;
  if (agent.config_json) {
    try { configParsed = JSON.stringify(JSON.parse(agent.config_json), null, 2); } catch { configParsed = agent.config_json; }
  }

  function loadBrain() {
    if (brain || brainLoading) return;
    setBrainLoading(true);
    api.get<{ brain: BrainSnapshot }>(`/cloud/admin/agents/${agent.machine_id}/brain`)
      .then(r => setBrain(r.brain))
      .catch(() => setBrainError('Sem snapshot de cérebro'))
      .finally(() => setBrainLoading(false));
  }

  function loadLLMUsage() {
    if (llmUsage || llmLoading) return;
    setLlmLoading(true);
    api.get<{ usage: LLMUsage | null }>(`/cloud/admin/agents/${agent.machine_id}/llm-usage`)
      .then(r => { if (r.usage) setLlmUsage(r.usage); else setLlmError('Sem dados de uso LLM'); })
      .catch(() => setLlmError('Erro ao carregar gastos LLM'))
      .finally(() => setLlmLoading(false));
  }

  // Load data when tab selected
  useEffect(() => { if (tab === 'brain') loadBrain(); }, [tab]);
  useEffect(() => { if (tab === 'llm') loadLLMUsage(); }, [tab]);

  const langMap: Record<string, string> = { 'pt': 'Português', 'pt-BR': 'Português', 'en': 'English', 'es': 'Español' };

  return (
    <div style={{ marginTop: 12 }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.bgHover}`, marginBottom: 12 }}>
        {TABS.map(t => (
          <div
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              color: tab === t.id ? t.color : T.textMuted,
              borderBottom: tab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >{t.label}</div>
        ))}
      </div>

      {/* Tab: Geral */}
      {tab === 'geral' && (
        <div style={{ display: 'grid', gap: 2 }}>
          <InfoRow label="Nome" value={agent.agent_name} />
          <InfoRow label="Modelo" value={agent.agent_provider && agent.agent_model ? `${agent.agent_provider}/${agent.agent_model}` : agent.agent_model} />
          <InfoRow label="Idioma" value={agent.agent_language ? (langMap[agent.agent_language] || agent.agent_language) : null} />
 <InfoRow label="Docker" value={agent.is_docker ? 'Sim' : 'Não'} />
          <InfoRow label="Node" value={agent.node_version} />
          <InfoRow label="OpenClaw Version" value={agent.openclaw_version} />
          <InfoRow label="OC Path" value={agent.oc_path} mono />
          <InfoRow label="Env Path" value={agent.env_path} mono />
          <InfoRow label="Config Path" value={agent.config_path} mono />
          <InfoRow label="Last Sync" value={agent.synced_at ? `${timeAgo(agent.synced_at)} (${agent.synced_at})` : null} />
          <InfoRow label="Last Auto-Sync" value={agent.last_auto_sync ? `${timeAgo(agent.last_auto_sync)} (${agent.last_auto_sync})` : null} />
          <InfoRow label="Máquina" value={agent.machine_display_name || agent.machine_name} />
          <InfoRow label="IP" value={agent.machine_ip} mono />
          <InfoRow label="Status" value={agent.machine_status} />
          <InfoRow label="Cliente" value={agent.customer_email} />
        </div>
      )}

      {/* Tab: Credenciais & Integrações */}
      {tab === 'creds' && (
        <div>
          {integrations.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 6 }}>INTEGRAÇÕES ({integrations.length})</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                {integrations.map(i => <Badge key={i} text={i} color={T.success} />)}
              </div>
            </div>
          )}
          {envKeys.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 6 }}>ENV KEYS ({envKeys.length})</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                {envKeys.map(k => <Badge key={k} text={k} color="#6366F1" />)}
              </div>
            </div>
          )}
          {configParsed && (
            <div>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 6 }}>CONFIG JSON</div>
              <pre style={{
                background: T.blackAlpha30, border: `1px solid ${T.border}`,
                borderRadius: 8, padding: 12, fontSize: 11, color: T.text,
                maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' as const,
                fontFamily: 'monospace', margin: 0,
              }}>{configParsed}</pre>
            </div>
          )}
          {integrations.length === 0 && envKeys.length === 0 && !configParsed && (
            <div style={{ color: T.textDisabled, fontSize: 12, padding: 20, textAlign: 'center' as const }}>Sem credenciais ou integrações</div>
          )}
        </div>
      )}

      {/* Tab: Cérebro */}
      {tab === 'brain' && (
        <div>
          {brainLoading && <div style={{ padding: 12 }}><SkeletonBar width="60%" height={12} /></div>}
          {brainError && <div style={{ color: T.danger, fontSize: 12, padding: 12 }}>{brainError}</div>}
          {brain && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 8 }}>
                BRAIN SNAPSHOT {brain.snapshot_at && <span style={{ fontWeight: 400 }}>— {timeAgo(brain.snapshot_at)}</span>}
              </div>
              <MdBlock title="SOUL.md" content={brain.soul_md} />
              <MdBlock title="USER.md" content={brain.user_md} />
              <MdBlock title="MEMORY.md" content={brain.memory_md} />
              <MdBlock title="TOOLS.md" content={brain.tools_md} />
            </div>
          )}
          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 8 }}>WORKSPACE FILES (agent_info)</div>
          <MdBlock title="AGENTS.md" content={agent.agents_md} />
          <MdBlock title="HEARTBEAT.md" content={agent.heartbeat_md} />
          <MdBlock title="IDENTITY.md" content={agent.identity_md} />
          <MdBlock title="BOOTSTRAP.md" content={agent.bootstrap_md} />
          {!brain && !brainLoading && !brainError && !agent.agents_md && !agent.heartbeat_md && !agent.identity_md && !agent.bootstrap_md && (
            <div style={{ color: T.textDisabled, fontSize: 12, padding: 20, textAlign: 'center' as const }}>Sem dados de cérebro</div>
          )}
        </div>
      )}

      {/* Tab: Canais de Comunicação */}
      {tab === 'channels' && (
        <div>
          {channels.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
              {channels.map(ch => {
 const info = CHANNEL_ICONS[ch.toLowerCase()] || { icon: '', color: T.textMuted };
                return (
                  <div key={ch} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    background: `${info.color}10`, borderRadius: 8, border: `1px solid ${info.color}25`,
                  }}>
                    <span style={{ fontSize: 18 }}>{info.icon}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: info.color, textTransform: 'capitalize' as const }}>{ch}</div>
                      <div style={{ fontSize: 10, color: T.textMuted }}>Conectado</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ color: T.textDisabled, fontSize: 12, padding: 20, textAlign: 'center' as const }}>Nenhum canal de comunicação configurado</div>
          )}
        </div>
      )}

      {/* Tab: Gastos LLM */}
      {tab === 'llm' && (
        <div>
          {llmLoading && <div style={{ padding: 12 }}><SkeletonBar width="50%" height={12} /></div>}
          {llmError && <div style={{ color: T.danger, fontSize: 12, padding: 12 }}>{llmError}</div>}
          {llmUsage && (
            <div>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                {[
                  { label: 'REQUISIÇÕES', value: String(llmUsage.totals.total_requests || 0), color: T.primary },
                  { label: 'CUSTO PROVEDOR', value: formatUSD(llmUsage.totals.total_cost || 0), color: T.success },
                  { label: 'COBRADO', value: formatUSD(llmUsage.totals.total_charged || 0), color: T.warning },
                  { label: 'TOKENS', value: formatTokens((llmUsage.totals.total_input_tokens || 0) + (llmUsage.totals.total_output_tokens || 0)), color: T.purple },
                ].map(c => (
                  <div key={c.label} style={{
                    padding: '10px 12px', borderRadius: 8,
                    background: `${c.color}10`, border: `1px solid ${c.color}25`,
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, letterSpacing: 0.5 }}>{c.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: c.color, marginTop: 2 }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {/* Per-model breakdown */}
              {llmUsage.by_model.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 8 }}>POR MODELO (últimos 30 dias)</div>
                  <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: T.bgOverlay }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' as const, color: T.textMuted, fontWeight: 600 }}>Modelo</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Reqs</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Input</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Output</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Custo</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Latência</th>
                        </tr>
                      </thead>
                      <tbody>
                        {llmUsage.by_model.map((m, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${T.bgCard}` }}>
                            <td style={{ padding: '6px 10px', color: T.text }}>
                              <span style={{ color: T.purple, fontWeight: 600 }}>{m.provider}</span>
                              <span style={{ color: T.textMuted }}>/</span>
                              <span>{m.model}</span>
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.text }}>{m.requests}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.text }}>{formatTokens(m.total_input_tokens)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.text }}>{formatTokens(m.total_output_tokens)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.success, fontWeight: 600 }}>{formatUSD(m.total_charged)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted }}>{Math.round(m.avg_latency)}ms</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Daily spending */}
              {llmUsage.daily.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 8 }}>GASTO DIÁRIO</div>
                  <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: T.bgOverlay }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' as const, color: T.textMuted, fontWeight: 600 }}>Data</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Reqs</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Tokens</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.textMuted, fontWeight: 600 }}>Cobrado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {llmUsage.daily.slice(0, 10).map((d, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${T.bgCard}` }}>
                            <td style={{ padding: '6px 10px', color: T.text }}>{d.date}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.text }}>{d.requests}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.text }}>{formatTokens(d.input_tokens + d.output_tokens)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' as const, color: T.warning, fontWeight: 600 }}>{formatUSD(d.charged)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {llmUsage && (llmUsage as any).note && (
            <div style={{ background: T.warningBg, border: `1px solid ${T.warningBgStrong}`, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: T.warning, marginTop: 8 }}>
              {(llmUsage as any).note}
            </div>
          )}
          {!llmUsage && !llmLoading && !llmError && (
            <div style={{ color: T.textDisabled, fontSize: 12, padding: 20, textAlign: 'center' as const }}>Sem dados de gastos LLM</div>
          )}
        </div>
      )}

      {/* Tab: Tools & Skills */}
      {tab === 'tools' && (
        <div>
          {tools.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 6 }}>TOOLS ({tools.length})</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                {tools.map(t => <Badge key={t} text={t} color={T.primary} />)}
              </div>
            </div>
          )}
          {skills.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, marginBottom: 6 }}>SKILLS ({skills.length})</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                {skills.map(s => <Badge key={s} text={s} color={T.warning} />)}
              </div>
            </div>
          )}
          {tools.length === 0 && skills.length === 0 && (
            <div style={{ color: T.textDisabled, fontSize: 12, padding: 20, textAlign: 'center' as const }}>Sem tools ou skills</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── SVG Icons for OS ── */
function ServerIcon({ os }: { os?: string }) {
  const o = (os || '').toLowerCase();
  if (o.includes('mac') || o.includes('darwin')) return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A3B1C6" strokeWidth="1.5"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M14.5 8c-.83 0-1.5.67-1.5 1.5V11h-2V9.5C11 8.67 10.33 8 9.5 8S8 8.67 8 9.5v5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V13h2v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-5c0-.83-.67-1.5-1.5-1.5z"/></svg>
  );
  if (o.includes('linux')) return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A3B1C6" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="9" cy="10" r="1.5" fill="#A3B1C6"/><circle cx="15" cy="10" r="1.5" fill="#A3B1C6"/><path d="M8 15c0 0 2 2 4 2s4-2 4-2"/></svg>
  );
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A3B1C6" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
  );
}

function AgentCard({ agent, expanded, onToggle, parseJson }: { agent: AgentRow; expanded: boolean; onToggle: () => void; parseJson: (v: string | null) => string[] }) {
  const tools = parseJson(agent.tools);
  const integrations = parseJson(agent.integrations);
  const envKeys = parseJson(agent.env_keys);
  const name = agent.agent_name || 'Sem nome';
  const fullModel = agent.agent_provider && agent.agent_model ? `${agent.agent_provider}/${agent.agent_model}` : '';

  return (
    <div style={{
      padding: 18, background: T.bgCard, borderRadius: 14,
      border: expanded ? '1px solid rgba(167,139,250,0.3)' : `1px solid ${T.bgHover}`,
      transition: 'border-color 0.2s',
    }}>
      <div onClick={onToggle} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
        <AgentAvatar name={name} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{name}</div>
          <div style={{ fontSize: 11, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
            {agent.machine_display_name || agent.machine_name || agent.machine_id}
            {agent.machine_ip && <span> · {agent.machine_ip}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {fullModel && <Badge text={fullModel} color="#A78BFA" />}
          {integrations.length > 0 && <Badge text={`${integrations.length} integ`} color={T.success} />}
          {envKeys.length > 0 && <Badge text={`${envKeys.length} env`} color="#6366F1" />}
          {tools.length > 0 && <Badge text={`${tools.length} tools`} color={T.primary} />}
          {agent.openclaw_version && <Badge text={`v${agent.openclaw_version}`} color={T.textMuted} />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-end', flexShrink: 0, gap: 2 }}>
          <span style={{ fontSize: 10, color: T.textDisabled }}>{agent.customer_email}</span>
          <span style={{ fontSize: 10, color: T.textDisabled }} title={agent.synced_at}>{timeAgo(agent.synced_at)}</span>
        </div>
        <span style={{ color: T.textMuted, fontSize: 14, marginLeft: 4 }}>{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && <ExpandedAgent agent={agent} parseJson={parseJson} />}
    </div>
  );
}

/* ── Grouped by Server view ── */
interface ServerGroup {
  key: string;
  hostname: string;
  ip: string | null;
  os: string | null;
  customer_email: string | null;
  agents: AgentRow[];
}

function groupByServer(agents: AgentRow[]): ServerGroup[] {
  const map = new Map<string, ServerGroup>();
  for (const a of agents) {
    const key = a.machine_name || a.machine_display_name || a.machine_id;
    if (!map.has(key)) {
      map.set(key, {
        key,
        hostname: a.machine_display_name || a.machine_name || a.machine_id,
        ip: a.machine_ip,
        os: null, // Could enrich from runners if needed
        customer_email: a.customer_email,
        agents: [],
      });
    }
    map.get(key)!.agents.push(a);
  }
  return Array.from(map.values()).sort((a, b) => b.agents.length - a.agents.length);
}

function ServerGroupCard({ group, expanded, onToggleAgent, parseJson }: {
  group: ServerGroup; expanded: string | null; onToggleAgent: (id: string) => void; parseJson: (v: string | null) => string[];
}) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{
      borderRadius: 16, border: '1px solid rgba(124,58,237,0.2)',
      overflow: 'hidden', background: 'rgba(124,58,237,0.03)',
    }}>
      {/* Server Header */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
          background: 'rgba(124,58,237,0.08)', cursor: 'pointer', userSelect: 'none',
          borderBottom: open ? '1px solid rgba(124,58,237,0.15)' : 'none',
        }}
      >
        <ServerIcon os={group.os || ''} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{group.hostname}</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>
            {group.ip || 'IP desconhecido'}
            <span style={{ margin: '0 6px', color: T.textDisabled }}>·</span>
            {group.agents.length} agente{group.agents.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <Badge text={`${group.agents.length} agente${group.agents.length !== 1 ? 's' : ''}`} color={T.purpleHover} />
          {group.customer_email && <span style={{ fontSize: 10, color: T.textDisabled }}>{group.customer_email}</span>}
        </div>
        <span style={{ color: T.purpleHover, fontSize: 14, marginLeft: 4 }}>{open ? '▾' : '▸'}</span>
      </div>

      {/* Agents inside */}
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 0 }}>
          {group.agents.map((a, i) => {
            const isExp = expanded === a.id;
            const name = a.agent_name || 'Sem nome';
            const fullModel = a.agent_provider && a.agent_model ? `${a.agent_provider}/${a.agent_model}` : '';
            const tools = parseJson(a.tools);
            const integrations = parseJson(a.integrations);
            const envKeys = parseJson(a.env_keys);

            return (
              <div key={a.id} style={{
                padding: '14px 18px',
                borderTop: i > 0 ? `1px solid ${T.bgCard}` : 'none',
                background: isExp ? 'rgba(167,139,250,0.05)' : 'transparent',
              }}>
                <div
                  onClick={() => onToggleAgent(a.id)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  <AgentAvatar name={name} size={30} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0 }}>
                    {fullModel && <Badge text={fullModel} color="#A78BFA" />}
                    {integrations.length > 0 && <Badge text={`${integrations.length} integ`} color={T.success} />}
                    {envKeys.length > 0 && <Badge text={`${envKeys.length} env`} color="#6366F1" />}
                    {tools.length > 0 && <Badge text={`${tools.length} tools`} color={T.primary} />}
                  </div>
                  <span style={{ fontSize: 10, color: T.textDisabled }} title={a.synced_at}>{timeAgo(a.synced_at)}</span>
                  <span style={{ color: T.textMuted, fontSize: 12 }}>{isExp ? '▾' : '▸'}</span>
                </div>
                {isExp && <ExpandedAgent agent={a} parseJson={parseJson} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── View Toggle Button ── */
function ViewToggle({ view, onChange }: { view: 'agents' | 'servers'; onChange: (v: 'agents' | 'servers') => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: T.bgOverlay, borderRadius: 10, padding: 3 }}>
      <Button variant={view === 'agents' ? 'primary' : 'ghost'} size="xs" onClick={() => onChange('agents')}>Agentes</Button>
      <Button variant={view === 'servers' ? 'primary' : 'ghost'} size="xs" onClick={() => onChange('servers')}>Servidores</Button>
    </div>
  );
}

export function AdminAgents() {
  if (!isLoggedIn.value) return <LoginPage />;

  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [view, setView] = useState<'agents' | 'servers'>('agents');

  useEffect(() => { loadAgents(); }, []);

  async function loadAgents() {
    try {
      const res = await api.get<{ agents: AgentRow[] }>('/cloud/admin/agents');
      setAgents(res.agents || []);
    } catch (e) {
      console.error('[AdminAgents] Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  function parseJson(val: string | null): string[] {
    if (!val) return [];
    try { return JSON.parse(val); } catch { return []; }
  }

  const customers = [...new Set(agents.map(a => a.customer_email).filter(Boolean))] as string[];
  const models = [...new Set(agents.map(a => a.agent_provider && a.agent_model ? `${a.agent_provider}/${a.agent_model}` : '').filter(Boolean))];

  const filtered = agents.filter(a => {
    if (search) {
      const q = search.toLowerCase();
      const matchName = (a.agent_name || '').toLowerCase().includes(q);
      const matchMachine = (a.machine_name || a.machine_display_name || '').toLowerCase().includes(q);
      const matchEmail = (a.customer_email || '').toLowerCase().includes(q);
      if (!matchName && !matchMachine && !matchEmail) return false;
    }
    if (filterCustomer && a.customer_email !== filterCustomer) return false;
    if (filterModel) {
      const fullModel = `${a.agent_provider || ''}/${a.agent_model || ''}`;
      if (fullModel !== filterModel) return false;
    }
    return true;
  });

  const serverGroups = groupByServer(filtered);
  const totalServers = serverGroups.length;

  if (loading) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header with view toggle */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
            {view === 'agents' ? 'Agentes' : 'Servidores'}
          </h1>
          <p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>
            {view === 'agents'
              ? `${agents.length} agente${agents.length !== 1 ? 's' : ''} sincronizado${agents.length !== 1 ? 's' : ''} com D1`
              : `${totalServers} servidor${totalServers !== 1 ? 'es' : ''} · ${agents.length} agente${agents.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const }}>
        <Input
          placeholder="Buscar por nome, máquina ou email..."
          value={search} onChange={setSearch}
          style={{ flex: '1 1 200px' }}
        />
        <select value={filterCustomer} onChange={(e: any) => setFilterCustomer(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, background: T.bgCard,
            border: `1px solid ${T.borderHover}`, color: T.text, fontSize: 12,
          }}>
          <option value="">Todos clientes</option>
          {customers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterModel} onChange={(e: any) => setFilterModel(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, background: T.bgCard,
            border: `1px solid ${T.borderHover}`, color: T.text, fontSize: 12,
          }}>
          <option value="">Todos modelos</option>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Nenhum agente encontrado</div>
      ) : view === 'agents' ? (
        /* ── Agents flat view ── */
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
          {filtered.map(a => (
            <AgentCard
              key={a.id} agent={a} parseJson={parseJson}
              expanded={expanded === a.id}
              onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
            />
          ))}
        </div>
      ) : (
        /* ── Servers grouped view ── */
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
          {serverGroups.map(g => (
            <ServerGroupCard
              key={g.key} group={g} expanded={expanded} parseJson={parseJson}
              onToggleAgent={(id) => setExpanded(expanded === id ? null : id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
